import { useEffect } from "react"
import { router } from "@inertiajs/react"

// The viewer's own cut-off and minimum excess value, never saved for the
// market. Precedence: the URL (?doc / ?min) wins and is copied into this
// browser; else what this browser remembers for the market is pushed into the
// URL so the server builds the list with it; else the server default.
export interface ListSettings {
  doc: number
  min: number
}

const storageKey = (marketSlug: string) => `push.review.list.${marketSlug}`

function readStored(marketSlug: string): ListSettings | null {
  try {
    const raw = localStorage.getItem(storageKey(marketSlug))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ListSettings>
    if (Number.isInteger(parsed.doc) && typeof parsed.min === "number" && parsed.min >= 0) {
      return { doc: parsed.doc as number, min: parsed.min }
    }
  } catch {
    // storage unavailable or corrupt — the server default stays
  }
  return null
}

function writeStored(marketSlug: string, settings: ListSettings) {
  try {
    localStorage.setItem(storageKey(marketSlug), JSON.stringify(settings))
  } catch {
    // storage unavailable — the URL still carries the choice
  }
}

const inUrl = () => {
  const query = new URLSearchParams(window.location.search)
  return query.has("doc") || query.has("min")
}

interface Options {
  marketSlug: string
  path: string
  // What the server built the list with on this render, and its defaults.
  current: ListSettings
  defaults: ListSettings
}

export function useListSettings({ marketSlug, path, current, defaults }: Options) {
  // Only the numbers that differ from the default go in the URL.
  const params = (settings: ListSettings) => ({
    doc: settings.doc === defaults.doc ? undefined : settings.doc,
    min: settings.min === defaults.min ? undefined : settings.min,
  })

  const navigate = (settings: ListSettings, extra: Record<string, unknown>, replace: boolean) =>
    router.get(path, { ...extra, ...params(settings) }, { preserveScroll: true, preserveState: true, replace })

  useEffect(() => {
    if (inUrl()) {
      writeStored(marketSlug, current)
      return
    }
    const stored = readStored(marketSlug)
    if (stored && (stored.doc !== current.doc || stored.min !== current.min)) {
      const query = Object.fromEntries(new URLSearchParams(window.location.search))
      navigate(stored, query, true)
    }
    // Run once per market: later renders reflect what we navigated to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketSlug])

  const apply = (settings: ListSettings, extra: Record<string, unknown>) => {
    writeStored(marketSlug, settings)
    navigate(settings, extra, false)
  }

  return { apply, params: params(current) }
}
